import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { LayoutDashboard, Users, Building, CreditCard, FileText, Settings, LogOut, ChevronLeft, ChevronRight, ShieldCheck, BarChart3, Menu, X, Briefcase, Wrench, Award, Calendar, FolderKanban, FileSignature, PieChart } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useMediaQuery } from '../../hooks/useMediaQuery';
export function Sidebar() {
  const {
    user,
    logout
  } = useAuth();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  if (!user) return null;
  const getNavItems = () => {
    switch (user.role) {
      case 'tenant':
        return [{
          name: 'Dashboard',
          icon: LayoutDashboard,
          path: '/tenant/dashboard'
        }, {
          name: 'Rent Request',
          icon: FileText,
          path: '/tenant/rent-request'
        }, {
          name: 'Payments',
          icon: CreditCard,
          path: '/tenant/payment-schedule'
        }, {
          name: 'Flexibility',
          icon: Calendar,
          path: '/tenant/payment-flexibility'
        }, {
          name: 'Documents',
          icon: FileText,
          path: '/tenant/documents'
        }, {
          name: 'Certificate',
          icon: Award,
          path: '/tenant/certificate'
        }, {
          name: 'Rent History',
          icon: BarChart3,
          path: '/tenant/rent-history'
        }, {
          name: 'Maintenance',
          icon: Wrench,
          path: '/tenant/maintenance'
        }, {
          name: 'Payroll',
          icon: Briefcase,
          path: '/tenant/payroll-integration'
        }, {
          name: 'Settings',
          icon: Settings,
          path: '/tenant/settings'
        }];
      case 'landlord':
        return [{
          name: 'Dashboard',
          icon: LayoutDashboard,
          path: '/landlord/dashboard'
        }, {
          name: 'Portfolio',
          icon: PieChart,
          path: '/landlord/portfolio'
        }, {
          name: 'Properties',
          icon: Building,
          path: '/landlord/properties'
        }, {
          name: 'Lease Generator',
          icon: FileSignature,
          path: '/landlord/lease-generator'
        }, {
          name: 'Applications',
          icon: FolderKanban,
          path: '/landlord/applications'
        }];
      case 'admin':
        return [{
          name: 'Dashboard',
          icon: LayoutDashboard,
          path: '/admin/dashboard'
        }, {
          name: 'Verification',
          icon: ShieldCheck,
          path: '/admin/verification'
        }, {
          name: 'Analytics',
          icon: BarChart3,
          path: '/admin/analytics'
        }, {
          name: 'Risk Management',
          icon: ShieldCheck,
          path: '/admin/risk-management'
        }];
      case 'employer':
        return [{
          name: 'Dashboard',
          icon: LayoutDashboard,
          path: '/employer/dashboard'
        }];
      default:
        return [];
    }
  };
  const navItems = getNavItems();
  const sidebarVariants = {
    expanded: {
      width: 240,
      x: 0
    },
    collapsed: {
      width: 80,
      x: 0
    },
    mobileClosed: {
      x: '-100%'
    },
    mobileOpen: {
      x: 0
    }
  };
  return <>
      {/* Mobile Menu Button */}
      <div className="lg:hidden" data-id="element-2540">
        {!isMobileOpen && <button onClick={() => setIsMobileOpen(true)} className="fixed top-4 left-4 z-[100] p-3 rounded-xl bg-white shadow-lg text-gray-700 hover:bg-blue-50 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all border border-gray-200" aria-label="Open menu" data-id="element-2541">
            <Menu size={24} strokeWidth={2} data-id="element-2542" />
          </button>}
      </div>

      {/* Mobile Overlay */}
      <AnimatePresence data-id="element-2543">
        {!isDesktop && isMobileOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={() => setIsMobileOpen(false)} className="fixed inset-0 bg-black/50 z-[90] lg:hidden backdrop-blur-sm" data-id="element-2544" />}
      </AnimatePresence>

      {/* Sidebar Container */}
      <motion.div className="fixed top-0 left-0 h-full bg-white border-r border-gray-200 z-[95] flex flex-col shadow-xl lg:shadow-none" style={{
      width: isDesktop ? isCollapsed ? 80 : 240 : 240
    }} variants={sidebarVariants} animate={isDesktop ? isCollapsed ? 'collapsed' : 'expanded' : isMobileOpen ? 'mobileOpen' : 'mobileClosed'} initial={isDesktop ? 'expanded' : 'mobileClosed'} transition={{
      type: 'spring',
      stiffness: 300,
      damping: 30
    }} data-id="element-2545">
        {/* Logo Area */}
        <div className="h-20 flex items-center justify-center border-b border-gray-100 relative bg-white px-4" data-id="element-2546">
          {!isCollapsed || !isDesktop ? <div className="flex items-center w-full justify-between" data-id="element-2547">
              <img src="https://cdn.magicpatterns.com/uploads/7EjErFNEa8ZC1es35aEHqt/RENTA_logo.png" alt="Renta" className="h-10 sm:h-12 w-auto object-contain transition-all duration-300" data-id="element-2548" />
              {!isDesktop && <button onClick={() => setIsMobileOpen(false)} className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-colors ml-2" data-id="element-2549">
                  <X size={24} data-id="element-2550" />
                </button>}
            </div> : <div className="p-2" data-id="element-2551">
              <img src="https://cdn.magicpatterns.com/uploads/nEUSW8VBjg336ZuLftpftq/RENTA_(1).png" alt="R" className="h-10 w-10 object-contain" data-id="element-2552" />
            </div>}

          {/* Collapse Toggle (Desktop only) */}
          {isDesktop && <button onClick={() => setIsCollapsed(!isCollapsed)} className="absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-6 bg-white border border-gray-200 rounded-full flex items-center justify-center text-gray-400 hover:text-blue-600 shadow-sm z-50 hover:scale-110 transition-transform" data-id="element-2553">
              {isCollapsed ? <ChevronRight size={14} data-id="element-2554" /> : <ChevronLeft size={14} data-id="element-2555" />}
            </button>}
        </div>

        {/* Navigation Items */}
        <div className="flex-1 overflow-y-auto py-4 space-y-1 px-3 scrollbar-hide" data-id="element-2556">
          {navItems.map(item => <NavLink key={item.path} to={item.path} onClick={() => !isDesktop && setIsMobileOpen(false)} className={({
          isActive
        }) => `flex items-center px-3 py-3 rounded-lg transition-all duration-200 group relative ${isActive ? 'bg-blue-50 text-blue-700 font-medium shadow-sm' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'}`} data-id="element-2557">
              <item.icon size={22} className={`flex-shrink-0 transition-colors ${isCollapsed && isDesktop ? 'mx-auto' : 'mr-3'}`} data-id="element-2558" />
              {(!isCollapsed || !isDesktop) && <span className="truncate text-sm font-medium" data-id="element-2559">
                  {item.name}
                </span>}

              {/* Tooltip for collapsed state */}
              {isCollapsed && isDesktop && <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50 transition-opacity shadow-lg" data-id="element-2560">
                  {item.name}
                </div>}
            </NavLink>)}
        </div>

        {/* User Profile & Logout */}
        <div className="p-4 border-t border-gray-100 bg-gray-50/50" data-id="element-2561">
          <div className={`flex items-center ${isCollapsed && isDesktop ? 'justify-center' : ''}`} data-id="element-2562">
            <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold border border-blue-200 flex-shrink-0 shadow-sm text-lg" data-id="element-2563">
              {user.name.charAt(0)}
            </div>
            {(!isCollapsed || !isDesktop) && <div className="ml-3 overflow-hidden" data-id="element-2564">
                <p className="text-sm font-medium text-gray-900 truncate" data-id="element-2565">
                  {user.name}
                </p>
                <p className="text-xs text-gray-500 capitalize truncate" data-id="element-2566">
                  {user.role}
                </p>
              </div>}
          </div>

          <button onClick={logout} className={`mt-4 flex items-center w-full px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors ${isCollapsed && isDesktop ? 'justify-center' : ''}`} data-id="element-2567">
            <LogOut size={20} className={isCollapsed && isDesktop ? '' : 'mr-3'} data-id="element-2568" />
            {(!isCollapsed || !isDesktop) && <span data-id="element-2569">Sign Out</span>}
          </button>
        </div>
      </motion.div>
    </>;
}